/* global _LTracker */

import axios from 'axios';
import moment from 'moment';
import { getNumberOfStations } from '../selectors/station';
import * as Utils from './utils';
import packageJson from '../package.alias.json';

export const server = process.env.REACT_APP_MODE; // 'local', 'staging', 'production'
export const apiUrl = getBaseURL(server);

const MAX_ATTEMPTS = 5;

function getBaseURL(s) {
  switch (s) {
    case 'local':
      return 'http://localhost:8080/api';
    case 'staging':
      return 'https://staging.api.ctrlv.ca/api';
    case 'alignet':
      return 'https://staging.api.ctrlv.ca/api';
    case 'production':
      return 'https://api.ctrlv.ca/api';
    default:
      console.error('No Server Specified!');
      throw new Error('You are not doing this right.');
  }
}

function makeSecCodeFourDigits(secCode) {
    return String(secCode).padStart(4, '0');
}

async function attemptLoop(url, requestType, object={}) {
    let attempts = 0;
    let lastError;
    while(++attempts <= MAX_ATTEMPTS) {
        let response;
        try {
            if(requestType === "get") response = await axios.get(url);
            else if(requestType === "post") response = await axios.post(url, object);

            if(response && response.data.success) {
                return {
                    success: true,
                    result: response.data.result
                }
            }
            console.log(response.data);
        } catch(err) {
            console.log(`Error attempting ${requestType}@${url}`);
            console.log("Attempt: ", attempts);
            console.error(err);

            lastError = err;

            // If the app gets a 500 response at any point, it really should be resetting as this means there was a server error while
            // trying to parse the data. If the specific function could be returned to instead of resetting the entire app state
            // that would be ideal (maybe to refresh the ReCAPTCHA token for example)
            if (err.response !== undefined && err.response.status === 500) {
                console.error(`Failed to perform request due to server issue. Flow cancelled.`);
                break;
            }
        }
    }

    return {
        success: false,
        error: lastError
    }
}

/* Gets the list of locations that are available to book */
export async function getLocations() {
    const endpointUrl = '/booking/getAvailableLocations';
    //const endpointUrl = '/booking/getAvailableLocationsForTesting'; // Releases all location regardless of is_open

    let response = await attemptLoop(apiUrl + endpointUrl, 'get');
    if(response.success) return response.result;

    _LTracker.push({
        error: response.error,
        message: "Getting locations. Retried, but it still failed :("
    });

    throw response.error;
}

/* Gets the days that are available to be booked at a specific location */
export async function getDates(locationId) {
    const endpointUrl = '/booking/getAvailableDates/';

    let response = await attemptLoop(apiUrl + endpointUrl + locationId, 'get');

    if(response.success) {
        return response.result.map((date) => {
            return moment(date.substr(0, 10));
        });
    }

    _LTracker.push({
        error: response.error,
        message: "Getting Dates. Retried, but it still failed :("
    });

    throw response.error;
}

/* Gets the stations that can be booked for a specific location on a specific day */
export async function getStations(locationId, date) {
    const endpointUrl = '/booking/getAvailableStations/';
    const pgDate = Utils.stringifyPGDate(date);

    if(pgDate === 'failed') { // returned invalid date
        throw response.error;
    }

    let response = await attemptLoop(apiUrl + endpointUrl + locationId + '/' + pgDate, 'get');

    if(response.success) {
        response.result.timeslots.forEach((timeslot) => {
            const utc_hours = parseInt(timeslot.utc_time.substr(11, 2), 10);
            const utc_minutes = parseInt(timeslot.utc_time.substr(14, 2), 10);
            const utc_dateField = timeslot.utc_time.substr(0, 10);

            timeslot.utc_moment = moment.utc(utc_dateField);
            timeslot.utc_moment.hours(utc_hours);
            timeslot.utc_moment.minutes(utc_minutes);

            const hours = parseInt(timeslot.time.substr(11, 2), 10);
            const minutes = parseInt(timeslot.time.substr(14, 2), 10);
            const dateField = timeslot.time.substr(0, 10);

            timeslot.moment = moment(dateField);
            timeslot.moment.hours(hours);
            timeslot.moment.minutes(minutes);
        });

        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Getting Stations. Retried, but it still failed :(",
        locationId: locationId,
        date: date
    });

    throw response.error;
}

// this was so dumb to write, took way too long but got it working
// it just checks that all item type images are in the array that they're supposed to be in
// without this, it will prematurely send back the array with nothing in it
// this stops that from happening because it keeps calling itself until the array is there with all images
function checkSequences(sequences) {
  return new Promise(function (resolve, reject) {
    if (!sequences || sequences.length === 0) reject('Error: No sequences for location');
    (function waitForSeq() {
      let imagesGood = false;
      let lengthsGood = false;
      for (let i = 0; i < sequences.length; i++) {
        const seq = sequences[i];

        if (seq.sequence_image_array.length === 0) {
          lengthsGood = false;
          break;
        }
        else lengthsGood = true;
        for (let j = 0; j < seq.sequence_image_array.length; j++) {
          const itmImg = seq.sequence_image_array[j];
          if (itmImg !== '' && itmImg) {
            imagesGood = true;
            var img = new Image();
            img.src = itmImg;
          }
          else {
            imagesGood = false;
            break;
          }
        }
        if(!seq.active) {
            imagesGood = true;
        }
      }
      if (imagesGood && lengthsGood) return resolve();
      setTimeout(waitForSeq, 30);
    })();
  })
}

export async function getSequences(locationId) {
    const endpointUrl = '/booking/sequencesForLocation/';

    let response = await attemptLoop(apiUrl + endpointUrl + locationId, 'get');

    if(response.success) {
        if (!response.result.sequences || response.result.sequences.length === 0) return 'No sequences to fetch';

        const sequences = response.result.sequences.filter(sequence => sequence.active);
        const local_server = (server === 'production') ? 'https://storage.googleapis.com/vault.api.ctrlv.ca' : 'https://storage.googleapis.com/vault.staging.api.ctrlv.ca';
        for (let i = 0; i < sequences.length; i++) {
            const seqSteps = sequences[i].sequence_steps;
            let imgArr = [];
            for (let j = 0; j < seqSteps.length; j++) {
                const seqStep = seqSteps[j];
                const seqStepImg = local_server + '/public/item_type_images/' + locationId + '/' + seqStep.sequence_item_id + '.png';
                if (seqStep.sequence_item_id > 0) {
                    try {
                        let seqImgResponse = await axios.get(seqStepImg);
                        if (seqImgResponse.status === 200 && (
                            seqImgResponse.headers['content-type'] === 'application/octet-stream' || seqImgResponse.headers['content-type'].match(/image\/.*/)
                        )) {
                            if (seqStep.sequence_item_id === 2 && (seqImgResponse.data !== '')) {
                                imgArr = [seqStepImg];
                                break;
                            }
                            imgArr[j] = (seqImgResponse.data !== '') ? seqStepImg : undefined;
                            continue;
                        }
                        // console.log(seqImgResponse);
                    } catch (err) {
                        console.warn(`Failed to fetch item image ${seqStep.sequence_item_id} for location ${locationId}`);
                        if (err !== undefined && err.response !== undefined && err.response.status === 403) {
                            console.warn(`This image likely hasn't been set`);
                        } else {
                            console.error(`This is a non-normal failure`);
                            console.error((err.response !== undefined) ? err.response : err);
                        }
                    };
                    imgArr[j] = local_server + '/public/item_type_images/0/' + seqStep.sequence_item_id + '.png'
                }
            }
            sequences[i].sequence_image_array = imgArr;
        }

        await checkSequences(sequences);

        return sequences;
    }

    _LTracker.push({
        error: response.error,
        message: "Getting Sequences. Retried, but it still failed :(",
        locationId: locationId
    });

    throw response.error;
}

export async function getPartyRooms(station, locationId, date) {
    const endpointUrl = '/booking/getAvailablePartyRooms/';
    const timeslotIds = [];

    Object.keys(station.selectedStations).forEach((timeslotId) => {
        const quantity = getNumberOfStations(station, parseInt(timeslotId, 10));
        if (quantity > 0) timeslotIds.push(parseInt(timeslotId, 10));
    });

    const pgDate = timeslotIds.length === 0 ? Utils.stringifyPGDate(date) : '';

    const timeslotJSON = {
        timeslotIds,
        date: pgDate,
        locationId
    };

    let response = await attemptLoop(apiUrl + endpointUrl, 'post', timeslotJSON);

    if(response.success) {
        response.result.timeslots.forEach((timeslot) => {
            const hours = parseInt(timeslot.time.substr(11, 2), 10);
            const minutes = parseInt(timeslot.time.substr(14, 2), 10);
            const dateField = timeslot.time.substr(0, 10);
            timeslot.moment = moment(dateField);
            timeslot.moment.hours(hours);
            timeslot.moment.minutes(minutes);

            const utc_hours = parseInt(timeslot.utc_time.substr(11, 2), 10);
            const utc_minutes = parseInt(timeslot.utc_time.substr(14, 2), 10);
            const utc_dateField = timeslot.utc_time.substr(0, 10);

            timeslot.utc_moment = moment.utc(utc_dateField);
            timeslot.utc_moment.hours(utc_hours);
            timeslot.utc_moment.minutes(utc_minutes);
        });
        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Getting Party Rooms. Retried, but it still failed :(",
        station: station,
        locationId: locationId,
        date: date
    });

    throw response.error;
}

export async function holdItems(state) {
    const endpointUrl = '/booking/holdItems';

    let timeslots = [];

    for (let ts in state.station.selectedStations) {
        if (state.station.selectedStations.hasOwnProperty(ts)) {
        let item = [];
        let items = state.station.selectedStations[ts];
        let extras = {};

        if (state.station.selectedExtra.hasOwnProperty(ts)) {
            extras = state.station.selectedExtra[ts];
        }
        if (items) {
            for (let itm in items) {
            if (items.hasOwnProperty(itm)) {

                let itmobj = {
                item_id: itm,
                quantity: items[itm],
                extra_quantity: items[itm]
                };

                itmobj.extra_quantity = 0;
                let itemQuant = items[itm];

                if (itemQuant && extras && extras.hasOwnProperty(itm)) {
                let extraQuant = extras[itm];
                if (extraQuant && extraQuant > itemQuant) {
                    itmobj.extra_quantity = extraQuant - itemQuant;
                }
                }
                item.push(itmobj);
            }
            }
        }
        timeslots.push({
            timeslot_id: ts,
            items: item
        });
        }
    }

    for (let ts in state.partyRoom.selectedPartyRooms) {
        if (state.partyRoom.selectedPartyRooms.hasOwnProperty(ts)) {
        let item = [];
        let items = state.partyRoom.selectedPartyRooms[ts];

        if (items) {
            for (let itm in items) {
            if (items.hasOwnProperty(itm)) {

                item.push({
                item_id: itm,
                quantity: items[itm]
                });
            }
            }
        }
        timeslots.push({
            timeslot_id: ts,
            items: item
        });
        }
    }

    var gift_card_info = [];
    if (state.giftCard.total_amount > 0) {
        for (let i of state.giftCard.array) {
        gift_card_info.push({
            giftcard_number: i.giftcard_number,
            amount: i.amount_to_charge
        });
        }
    }

    let obj = {};

    if(state.checkout.GTIReceipt) {
        obj.costa_rica_extra = {
        customer_number: state.checkout.cusNumber,
        provincia_id: state.checkout.provinciaId,
        canton_id: state.checkout.cantonId,
        distrito_id: state.checkout.distritoId,
        barrio_id: state.checkout.barrioId,
        direccion: state.checkout.direccion,
        tipo_ident: state.checkout.tipoIdent,
        tipo_doc: state.checkout.tipoDoc,
        area_telefono: state.checkout.areaTelefono,
        num_telefono: state.checkout.numTelefono,
        correo: state.checkout.correo,
        full_name: state.checkout.fullName,
        }
    }

    const itemsJSON = {
        first_name: state.checkout.firstName,
        last_name: state.checkout.lastName,
        email_address: state.checkout.email,
        phone_number: state.checkout.phone,

        ...obj, // conditionally includes costa_rica_extra

        captcha_response: state.checkout.captchaResponse,
        captcha_version: 'Booking|' + packageJson.version,
        timeslots: timeslots,
        best_discount_id: state.discount.selectedDiscountId || '0',
        discount_promo_code: state.discount.selectedPromoCode || '',
        location_id: state.bookingLocation.selectedLocationId,
        force_promo_code: state.discount.forcePromoCode,
        giftcards: gift_card_info,
        young_children: state.checkout.hasYoungChildren
    };

    let response = await attemptLoop(apiUrl + endpointUrl, 'post', itemsJSON);

    if(response.success) {
        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Holding Items. Retried, but it still failed :(",
        items: itemsJSON
    });

    throw response.error;
}

export async function getDiscounts(locationId) {
    const endpointUrl = '/booking/activeDiscountsForLocation/';

    let response = await attemptLoop(apiUrl + endpointUrl + locationId, 'get');

    if (response.success) {
        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Getting Discounts. Retried, but it still failed :(",
        locationId: locationId
    });

    throw response.error;
}

export async function checkPromo(promo, locationId) {
    const endpointUrl = '/booking/checkAvailablePromo?';
    const promoCode = 'promo_code=' + promo;
    const loc = 'location=' + locationId;

    let response = await attemptLoop(apiUrl + endpointUrl + promoCode + '&' + loc, 'get');

    if(response.success) {
        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Checking Promos. Retried, but it still failed :(",
        promo: promo,
        locationId: locationId
    });

    throw response.error;
}

export async function checkGiftCard(cardn, secCode) {
    const endpointUrl = '/booking/checkGiftCardInfo?';
    const cardNumber = `card_number=${cardn}`;
    const pinNumber = `&pin_number=${pinGeneration(cardn)}`;
    let securityCode = !secCode ? '' : `&security_code=${makeSecCodeFourDigits(secCode)}`;

    let response = await attemptLoop(apiUrl + endpointUrl + cardNumber + pinNumber + securityCode, 'get');

    if(response.success) {
        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Checking Gift Cards. Retried, but it still failed :(",
        cardn: cardn
    });

    throw response.error;
}

export async function getCheckoutInformation(locationId) {
    const endpointUrl = '/booking/checkoutInfo/';

    let response = await attemptLoop(apiUrl + endpointUrl + locationId, 'get');

    if(response.success) {
        return response.result;
    }

    _LTracker.push({
        error: response.error,
        message: "Getting Checkout Information. Retried, but it still failed :(",
        locationId: locationId
    });

    throw response.error;
}


function pinGeneration(cn) {
    let hash = 0, i, chr;
    if (cn.length === 0) return hash;
    for (i = 0; i < cn.length; i++) {
        chr = cn.charCodeAt(i);
        hash = ((hash << 5) - hash) + chr;
        hash |= 0;
    }
    return hash;
}

// Idea for this function is that once they login, we'll use the information from that and get their points card
export function getPointsCard(response, loginType) {
  const endpointUrl = '/portalauth/getPointsCard';
  let profile_id = '?profile_id=';
  let accessToken = '';
  let idToken = '';
  let userId = '';

  if (loginType === "google") {
    // Setting headers to allow for api request
    accessToken = response.googleResponse.access_token;
    idToken = response.userInfo.tokenId;
    userId = response.userInfo.sub;
  }
  else if (loginType === "facebook") {
    // Setting headers to allow for api request
    accessToken = response.accessToken;
    idToken = response.signedRequest;
    userId = response.id;
  }

  profile_id = profile_id + userId;

  return new Promise((resolve, reject) => {
    axios.get(apiUrl + endpointUrl + profile_id, {
      headers: {
        "access-token": accessToken,
        "id-token": idToken,
        "login-type": loginType,
        "user-id": userId,
      }
    })
      .then(result => {
        resolve(result.data.result);
      })
      .catch(error => {
        console.error(error);
        reject(error);
      })
  });
}

export async function getProvincia() {
	const endpointURL = '/booking/costarica/getProvincias'
	try {
		let response = await axios.get(apiUrl + endpointURL);

		if(response.data.success) {
			return response.data.result;
		}

	} catch(err) {
		console.log("Error getting provincia");
		console.log(err);
	}
}

export async function getCantons(provinciaId) {
	const endpointURL = '/booking/costarica/getCantons?provincia_id=' + provinciaId
	try {
		let response = await axios.get(apiUrl + endpointURL);

		if(response.data.success) {
			return response.data.result;
		}

	} catch(err) {
		console.log("Error getting Cantons");
		console.log(err);
	}
}

export async function getDistritos(provinciaId, cantonId) {
	const endpointURL = `/booking/costarica/getDistritos?provincia_id=${provinciaId}&canton_id=${cantonId}`
	try {
		let response = await axios.get(apiUrl + endpointURL);

		if(response.data.success) {
			return response.data.result;
		}

	} catch(err) {
		console.log("Error getting Distritos");
		console.log(err);
	}
}

export async function getBarrios(provinciaId, cantonId, distritoId) {
	const endpointURL = `/booking/costarica/getBarrios?provincia_id=${provinciaId}&canton_id=${cantonId}&distrito_id=${distritoId}`;
	try {
		let response = await axios.get(apiUrl + endpointURL);

		if(response.data.success) {
			return response.data.result;
		}

	} catch(err) {
		console.log("Error getting Barrios");
		console.log(err);
	}
}


export async function paymentCompleteRedirect(purchaseCode) {
    const endpointUrl = `/booking/export/redirectPaymentCompleteDirectly`;

    let response = await attemptLoop(`${apiUrl}${endpointUrl}?InvoiceID=${purchaseCode}`, 'post');
    console.log(`attemptloop response:`);
    console.log(response);
    if(response.success) {
        window.location.href = response.result.redirect;
        return response.result.redirect;
    }

    throw response.error;
}
